import {ApolloClient, gql} from '@apollo/client';
import {errorResponse, NetworkRequestStatus, NetworkResult} from './NetworkResult';
import {Duration} from '../Domain/Duration';
import {formatISO} from 'date-fns';
import {CalendarEvent, RsvpResponse} from '../Models/CalendarEvent';
import {RecurrenceRule} from '../Models/ScheduledEvent';
import {ScheduledEventUpdateInput} from '../Models/ScheduledEventUpdateInput';
import {RecurringEventUpdateType} from '../Models/RecurringEventUpdateType';

export class EventsService {
    constructor(private client: ApolloClient<any>) {}

    public async createScheduledEvent(
        crewId: number,
        title: string,
        description: string,
        startAt: Date,
        duration: Duration,
        recurrenceRule: RecurrenceRule | null,
    ): Promise<
        NetworkResult<{
            id: number;
        }>
    > {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    mutation (
                        $crewId: Int!
                        $title: String!
                        $description: String!
                        $startAtIso8601: String!
                        $durationMillis: Int!
                        $recurrenceRule: RecurrenceRuleInput
                    ) {
                        createScheduledEvent(
                            crew_id: $crewId
                            title: $title
                            description: $description
                            start_at_iso8601: $startAtIso8601
                            duration_millis: $durationMillis
                            recurrence_rule: $recurrenceRule
                        ) {
                            id
                        }
                    }
                `,
                variables: {
                    crewId: crewId,
                    title: title,
                    description: description,
                    startAtIso8601: formatISO(startAt),
                    durationMillis: duration.milliseconds,
                    recurrenceRule:
                        recurrenceRule === null
                            ? null
                            : {
                                  frequency: recurrenceRule.frequency,
                                  end_at_iso8601:
                                      recurrenceRule.endAt === null ? null : formatISO(recurrenceRule.endAt),
                              },
                },
                fetchPolicy: 'network-only',
            });

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return {
                status: NetworkRequestStatus.Success,
                data: {
                    id: apolloQueryResult.data.createScheduledEvent.id,
                },
            };
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async updateScheduledEvent(
        input: ScheduledEventUpdateInput,
        title: string,
        description: string,
        startAt: Date,
        duration: Duration,
    ): Promise<NetworkResult<{id: number}>> {
        try {
            const date = RecurringEventUpdateType.All === input.type ? null : input.date;
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    mutation (
                        $id: Int!
                        $updateType: RecurringEventUpdateType!
                        $dateIso8601: String
                        $title: String!
                        $description: String!
                        $startAtIso8601: String!
                        $durationMillis: Int!
                    ) {
                        updateScheduledEvent(
                            id: $id
                            update_type: $updateType
                            date_iso8601: $dateIso8601
                            title: $title
                            description: $description
                            start_at_iso8601: $startAtIso8601
                            duration_millis: $durationMillis
                        ) {
                            id
                        }
                    }
                `,
                variables: {
                    id: input.id,
                    dateIso8601: date ? formatISO(date) : null,
                    updateType: input.type,
                    title: title,
                    description: description,
                    startAtIso8601: formatISO(startAt),
                    durationMillis: duration.milliseconds,
                },
                fetchPolicy: 'network-only',
            });

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return {
                status: NetworkRequestStatus.Success,
                data: {
                    id: apolloQueryResult.data.updateScheduledEvent.id,
                },
            };
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async deleteScheduledEvent(input: ScheduledEventUpdateInput): Promise<NetworkResult<any>> {
        try {
            const date = RecurringEventUpdateType.All === input.type ? null : input.date;

            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    mutation ($id: Int!, $dateIso8601: String, $deleteType: RecurringEventUpdateType!) {
                        deleteScheduledEvent(id: $id, date_iso8601: $dateIso8601, delete_type: $deleteType) {
                            id
                        }
                    }
                `,
                variables: {
                    id: input.id,
                    deleteType: input.type,
                    dateIso8601: date ? formatISO(date) : null,
                },
                fetchPolicy: 'network-only',
            });

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return {
                status: NetworkRequestStatus.Success,
                data: null,
            };
        } catch (e) {
            return {
                status: NetworkRequestStatus.Error,
            };
        }
    }

    public async get(from: Date, till: Date): Promise<NetworkResult<CalendarEvent[]>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: gql`
                    query ($fromIso8601: String!, $tillIso8601: String!) {
                        calendarEvents(from_iso8601: $fromIso8601, till_iso8601: $tillIso8601) {
                            crew_id
                            scheduled_event_id
                            title
                            description
                            starts_at_iso8601
                            timezone
                            duration_millis
                            scheduledEvent {
                                id
                                crew_id
                                start_at_iso8601
                                duration_millis
                                recurrence_rule {
                                    frequency
                                    end_at_iso8601
                                }
                            }
                            rsvpStatuses {
                                user_id
                                response
                            }
                        }
                    }
                `,
                variables: {
                    fromIso8601: formatISO(from),
                    tillIso8601: formatISO(till),
                },
                fetchPolicy: 'network-only',
            });

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return {
                status: NetworkRequestStatus.Success,
                data: apolloQueryResult.data.calendarEvents.map((event: any) => {
                    return this.map(event);
                }),
            };
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    private map(event: any) {
        return {
            crewId: parseInt(event.crew_id, 10),
            scheduledEventId: event.scheduled_event_id,
            title: event.title,
            description: event.description,
            startAt: new Date(event.starts_at_iso8601),
            timezone: event.timezone,
            duration: Duration.fromMilliseconds(event.duration_millis),
            scheduledEvent: {
                id: event.scheduledEvent.id,
                crewId: parseInt(event.scheduledEvent.crew_id, 10),
                title: event.scheduledEvent.title,
                description: event.scheduledEvent.description,
                startAt: new Date(event.scheduledEvent.start_at_iso8601),
                duration: Duration.fromMilliseconds(event.scheduledEvent.duration_millis),
                recurrenceRule: event.scheduledEvent.recurrence_rule
                    ? {
                          frequency: event.scheduledEvent.recurrence_rule.frequency,
                          endAt: new Date(event.scheduledEvent.recurrence_rule.end_at_iso8601),
                      }
                    : null,
            },
            rsvpStatuses: event.rsvpStatuses.map((rsvpStatus: any) => {
                return {
                    userId: parseInt(rsvpStatus.user_id, 10),
                    response: rsvpStatus.response,
                };
            }),
        };
    }

    public async rsvpToCalendarEvent(
        scheduledEventId: number,
        userId: number,
        startsAt: Date,
        rsvpResponse: RsvpResponse,
    ): Promise<NetworkResult<any>> {
        const apolloQueryResult = await this.client.mutate({
            mutation: gql`
                mutation (
                    $scheduledEventId: Int!
                    $userId: Int!
                    $datetimeIso8601: String!
                    $response: RsvpStatusResponse!
                ) {
                    rsvpToCalendarEvent(
                        scheduled_event_id: $scheduledEventId
                        user_id: $userId
                        datetime_iso8601: $datetimeIso8601
                        response: $response
                    ) {
                        scheduled_event_id
                        user_id
                        response
                    }
                }
            `,
            variables: {
                scheduledEventId: scheduledEventId,
                userId: userId,
                datetimeIso8601: formatISO(startsAt),
                response: rsvpResponse,
            },
            fetchPolicy: 'network-only',
        });

        if (apolloQueryResult.errors) {
            return errorResponse();
        }

        return {
            status: NetworkRequestStatus.Success,
            data: {
                scheduledEventId: apolloQueryResult.data.rsvpToCalendarEvent.scheduled_event_id,
                userId: apolloQueryResult.data.rsvpToCalendarEvent.user_id,
                response: apolloQueryResult.data.rsvpToCalendarEvent.response,
            },
        };
    }
}
